import { FC, useEffect } from "react";
import Modal from "react-modal";
import close from "assets/images/close.svg";
import Button from "components/particles/primary-button";
import { Container, StyledReactModal } from "./style";
import { useSelector } from "react-redux";
import { dispatch, RootState } from "store";
import { setModalClosed } from "store/user-slice";
import { useForm } from "react-hook-form";
import { StripeToWiseTransferDTO } from "utils/helpers/models/stripeToWiseTransfer.dto";
import { useWiseTransfers } from "containers/wise-transfers/useHook";
import FormErrorMessage from "components/particles/forms/form-error-message";

interface AddWiseTransferModalProps {
  data: StripeToWiseTransferDTO | undefined;
  id: number;
  onSuccess: () => void;
  updateList: (updatedTransfer: StripeToWiseTransferDTO) => void;
}

const AddWiseTransferModal: FC<AddWiseTransferModalProps> = ({
  data,
  id,
  onSuccess,
  updateList,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    getValues,
  } = useForm<StripeToWiseTransferDTO>();

  const { postTransfer, putTransfer } = useWiseTransfers();
  const { wiseTransfersModalIsOpen, isLoading } = useSelector(
    (state: RootState) => state.user
  );

  const customStyles = {
    overlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999,
    },
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      transform: "translate(-50%, -50%)",
      borderRadius: "3rem",
      fontSize: "2rem",
      width: "68.5rem",
      position: 'relative' as const,
      zIndex: 1000,
    },
  };

  function closeModal() {
    reset();
    dispatch(setModalClosed("WiseTransfers"));
  }

  const submit = async (formData: StripeToWiseTransferDTO) => {
    try {
      const amount = typeof formData.amount === 'number'
        ? formData.amount
        : typeof formData.amount === 'string'
          ? parseFloat(formData.amount)
          : 0;

      if (isNaN(amount) || amount < 0) {
        console.error("Invalid amount value - must be a positive number");
        return;
      }

      if (formData.date) {
        const selectedDate = new Date(formData.date);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (selectedDate > today) {
          console.error("Date cannot be in the future");
          return;
        }
      }

      if (id !== 0) {
        await putTransfer(id, {
          amount: amount,
          date: formData.date,
        });
        updateList({
          id,
          amount: amount,
          date: formData.date,
        });
      } else {
        await postTransfer({
          amount: amount,
          date: formData.date,
        });
      }
      await onSuccess();
    } catch (error) {
      console.error("Error submitting transfer:", error);
    }
  };

  useEffect(() => {
    if (data && id !== 0) {
      const amount = typeof data.amount === 'number'
        ? data.amount
        : typeof data.amount === 'string'
          ? parseFloat(data.amount)
          : 0;
      setValue("amount", isNaN(amount) ? 0 : amount);

      let dateValue = "";
      if (data.date) {
        try {
          const dateObj = new Date(data.date);
          if (!isNaN(dateObj.getTime())) {
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            dateValue = `${year}-${month}-${day}`;
          }
        } catch (error) {
          console.error("Error parsing date:", error);
        }
      }
      setValue("date", dateValue);
    } else {
      setValue("amount", 0);
      setValue("date", "");
    }
  }, [data, id, setValue]);

  return (
    <StyledReactModal
      isOpen={wiseTransfersModalIsOpen}
      onRequestClose={closeModal}
      style={customStyles}
      contentLabel="Wise Transfer Modal"
    >
      <Container onSubmit={handleSubmit(submit)}>
        <button className="close-button" onClick={closeModal}>
          <img src={close} alt="close" />
        </button>
        <div className="model-container">
          <div className="popup-title">
            <h1>{id !== 0 ? "Update Wise Transfer" : "Add Wise Transfer"}</h1>
          </div>
          <div className="add-transfer-container">
            <div>
              <input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="Amount"
                {...register("amount", {
                  required: "Amount is required",
                  min: {
                    value: 0.01,
                    message: "Amount must be greater than 0",
                  },
                  validate: (value) => {
                    const numValue = typeof value === 'number' ? value : parseFloat(value);
                    if (isNaN(numValue) || numValue < 0) {
                      return "Amount cannot be negative";
                    }
                    return true;
                  },
                })}
              />
              <FormErrorMessage error={errors.amount} />
            </div>
            <div>
              <input
                type="date"
                placeholder="Date"
                max={new Date().toISOString().split('T')[0]}
                {...register("date", {
                  required: "Date is required",
                  validate: (value) => {
                    if (!value) return "Date is required";
                    const selectedDate = new Date(value);
                    const today = new Date();
                    today.setHours(23, 59, 59, 999);
                    if (selectedDate > today) {
                      return "Date cannot be in the future";
                    }
                    return true;
                  },
                })}
              />
              <FormErrorMessage error={errors.date} />
            </div>
          </div>

          <div className="add-button">
            <Button
              title={id !== 0 ? "Update" : "Add"}
              width="51.2rem"
              backgroundcolor="var(--primary)"
              fill="true"
              type="submit"
              disabled={isLoading}
            />
          </div>
        </div>
      </Container>
    </StyledReactModal>
  );
};

export default AddWiseTransferModal;

